import { useEffect, useMemo, useRef, useState } from 'react'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import {
  AlertTriangle,
  LayoutGrid,
  MoreHorizontal,
  PenLine,
  Plus,
  Save,
} from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { usePosLayout, useSavePosLayout } from '../hooks/use-pos-layout'
import {
  GRID_LIMITS,
  TILE_COLORS,
  clampGridValue,
  findIneligibleAddonSkus,
  getTileState,
  isBlockingTileState,
  newId,
  resolveTileProduct,
} from '../constants'
import { LayoutTile } from '../components/layout-tile'
import { ProductLibrary } from '../components/product-library'
import { MissingFromLayoutPanel } from '../components/missing-from-layout-panel'
import { CheckoutAddonsCard } from '../components/checkout-addons-card'
import { findMissingFromLayout, findNextFreeCell } from '../menu-diff'
import { useMenuProducts } from '../hooks/use-menu-products'
import type { MissingMenuProduct } from '../menu-diff'
import type { TileProduct } from '../constants'
import type {
  PosLayout,
  PosLayoutPage,
  PosLayoutTile,
  Product,
} from '@/api/types'
import { productsApi } from '@/api/endpoints/products'
import { pricesApi } from '@/api/endpoints/prices'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import {
  useProductLookup,
  useProducts,
} from '@/features/products/hooks/use-products'
import { useCategories } from '@/features/categories/hooks/use-categories'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { usePermissions } from '@/shared/hooks/use-permissions'

const DEFAULT_LAYOUT: PosLayout = { version: 1, pages: [] }

type GridCellData = { row: number; col: number }

function findTileByPosition(page: PosLayoutPage, row: number, col: number) {
  return page.tiles.find((tile) => tile.row === row && tile.col === col)
}

// --- GridCell (small, stays inline) ---

function GridCell({
  row,
  col,
  tile,
  tileProduct,
  onDrop,
  onRemove,
  onColorChange,
  onSetPrice,
  canEdit,
}: {
  row: number
  col: number
  tile?: PosLayoutTile
  tileProduct?: TileProduct
  onDrop: (source: Record<string, unknown>, cell: GridCellData) => void
  onRemove: (tileId: string) => void
  onColorChange: (tileId: string, color: string) => void
  onSetPrice: (sku: string) => void
  canEdit: boolean
}) {
  const cellRef = useRef<HTMLDivElement>(null)
  const [isOver, setIsOver] = useState(false)

  useEffect(() => {
    if (!canEdit) return
    const el = cellRef.current
    invariant(el)
    return dropTargetForElements({
      element: el,
      canDrop: ({ source }) =>
        source.data.type === 'tile' || source.data.type === 'product',
      getData: () => ({ type: 'cell', row, col }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: ({ source }) => {
        setIsOver(false)
        onDrop(source.data, { row, col })
      },
    })
  }, [row, col, onDrop, canEdit])

  return (
    <div
      ref={cellRef}
      className={`relative rounded-xl border border-dashed p-2 min-h-[96px] transition-colors ${
        isOver ? 'border-primary bg-primary/5' : 'border-border/60'
      }`}
    >
      {tile && tileProduct ? (
        <LayoutTile
          tile={tile}
          product={
            tileProduct.status === 'found' ? tileProduct.product : undefined
          }
          tileState={getTileState(tileProduct)}
          availableQty={
            tileProduct.status === 'found'
              ? tileProduct.product.availableQty
              : undefined
          }
          onRemove={onRemove}
          onColorChange={onColorChange}
          onSetPrice={onSetPrice}
          canEdit={canEdit}
        />
      ) : (
        <span className="text-xs text-muted-foreground">
          {row},{col}
        </span>
      )}
    </div>
  )
}

// --- Main Page ---

export function PosLayoutPage() {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const queryClient = useQueryClient()
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<
    number | null
  >(null)
  const [layout, setLayout] = useState<PosLayout>(DEFAULT_LAYOUT)
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const [newPageName, setNewPageName] = useState('')
  // One right-hand column, two panels: the product library and the menu diff.
  const [drawer, setDrawer] = useState<'library' | 'diff' | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showInactive, setShowInactive] = useState(false)
  const [libraryPageSize, setLibraryPageSize] = useState(30)
  const [dirty, setDirty] = useState(false)
  const { can } = usePermissions()
  const canEdit = can(ACTIONS.POS_LAYOUT_EDIT)
  // Stock and menus are separate permissions from layout editing. Ask for neither without them:
  // a refused stock lookup would cost the editor its prices and tile states as well.
  const canViewStock = can(ACTIONS.INVENTORY_VIEW)
  const canViewMenus = can(ACTIONS.MENU_VIEW)

  // Generic confirmation dialog
  const [confirmation, setConfirmation] = useState<{
    title: string
    description: string
    confirmLabel?: string
    destructive?: boolean
    onConfirm: () => void
  } | null>(null)

  // Rename dialog
  const [renameDialog, setRenameDialog] = useState<{ pageId: string } | null>(
    null,
  )
  const [renameInput, setRenameInput] = useState('')

  // Activation prompt state
  const [activationPending, setActivationPending] = useState<{
    sku: string
    productName: string
    row: number
    col: number
  } | null>(null)
  const [activating, setActivating] = useState(false)

  // Price dialog state
  const [priceDialog, setPriceDialog] = useState<{
    sku: string
    productName: string
  } | null>(null)
  const [priceInput, setPriceInput] = useState('')
  const [savingPrice, setSavingPrice] = useState(false)

  // Data fetching
  const { data: restaurantsData } = useRestaurants()
  const { data: layoutData, isLoading } = usePosLayout(selectedRestaurantId)
  const saveLayout = useSavePosLayout(selectedRestaurantId)
  const { data: categoriesData } = useCategories()

  const libraryStatus = showInactive ? 'all' : 'active'
  const { data: productsData, isLoading: productsLoading } = useProducts({
    restaurantId: selectedRestaurantId ?? undefined,
    search: searchQuery || undefined,
    categoryCode: selectedCategory !== 'all' ? selectedCategory : undefined,
    status: libraryStatus,
    pageSize: libraryPageSize,
    // A merged duplicate (CHG-2026-09-duplicate-product-merge) cannot be placed on a new tile —
    // only its primary can.
    excludeMerged: true,
  })

  // Reset page size when filters change
  useEffect(() => {
    setLibraryPageSize(30)
  }, [searchQuery, selectedCategory, showInactive, selectedRestaurantId])

  // The SKUs this layout references — the exact set the editor needs to resolve. Derived from the
  // live layout so a freshly placed tile (or a freshly added add-on) resolves too, and sorted
  // inside the hook so the query key is stable while tiles are dragged around.
  const layoutSkus = useMemo(
    () => [
      ...new Set([
        ...layout.pages.flatMap((p) => p.tiles.map((t) => t.sku)),
        ...(layout.checkoutAddonSkus ?? []),
      ]),
    ],
    [layout],
  )

  const {
    data: lookupData,
    isPending: lookupPending,
    isError: lookupFailed,
  } = useProductLookup(
    layoutSkus,
    selectedRestaurantId ?? undefined,
    canViewStock,
  )

  // Open-price products are not enumerable from the layout, so they need their own narrow query
  // rather than a slice of the catalogue. There are only ever a handful — but that was true of the
  // catalogue too, so the card reports it if the page ever fails to hold them all.
  const { data: openPriceData } = useProducts({
    restaurantId: selectedRestaurantId ?? undefined,
    status: 'active',
    openPrice: true,
    excludeMerged: true,
  })

  const {
    data: menuProductsData,
    isLoading: menuProductsLoading,
    isError: menuProductsFailed,
  } = useMenuProducts(selectedRestaurantId, { enabled: canViewMenus })

  // Diffed against the layout in the editor, not the saved one, so a placement empties the list
  // immediately and removing a tile puts it back.
  const missingFromLayout = useMemo(
    () => findMissingFromLayout(menuProductsData?.items ?? [], layout),
    [menuProductsData, layout],
  )

  const restaurants = restaurantsData?.items || []
  const categories = categoriesData?.items || []
  const products = productsData?.items || []
  // The library caps its page size, so tell the user when there is more than it can show.
  const libraryTruncated =
    (productsData?.pageSize ?? libraryPageSize) < libraryPageSize

  const productMap = useMemo(() => {
    const map = new Map<string, Product>()
    for (const product of lookupData?.items || []) {
      map.set(product.sku, product)
    }
    return map
  }, [lookupData])

  // Resolving is only "pending" while a lookup is actually in flight. A disabled query also reports
  // `isPending`, so require the inputs that enable it rather than trusting the flag alone.
  const resolvePending =
    layoutSkus.length > 0 && selectedRestaurantId !== null && lookupPending
  const tileProductFor = (sku: string) =>
    resolveTileProduct(sku, productMap, resolvePending)

  const openPriceProducts = openPriceData?.items || []
  const openPriceTruncated =
    (openPriceData?.total ?? 0) > openPriceProducts.length

  useEffect(() => {
    if (layoutData?.layout) {
      setLayout(layoutData.layout)
      setDirty(false)
    } else if (selectedRestaurantId) {
      setLayout(DEFAULT_LAYOUT)
      setDirty(false)
    }
  }, [layoutData, selectedRestaurantId])

  useEffect(() => {
    if (!layout.pages.length) {
      setSelectedPageId(null)
      return
    }
    if (!selectedPageId || !layout.pages.some((p) => p.id === selectedPageId)) {
      setSelectedPageId(layout.pages[0].id)
    }
  }, [layout.pages, selectedPageId])

  const selectedPage = layout.pages.find((p) => p.id === selectedPageId) || null

  function updatePage(
    pageId: string,
    updater: (page: PosLayoutPage) => PosLayoutPage,
  ) {
    setLayout((prev) => ({
      ...prev,
      pages: prev.pages.map((p) => (p.id === pageId ? updater(p) : p)),
    }))
    setDirty(true)
  }

  // --- Page management handlers ---

  function handleAddPage() {
    if (!canEdit) return
    const name = newPageName.trim()
    if (!name) return
    const page: PosLayoutPage = {
      id: newId(),
      name,
      grid: { columns: 6, rows: 8 },
      tiles: [],
    }
    setLayout((prev) => ({ ...prev, pages: [...prev.pages, page] }))
    setSelectedPageId(page.id)
    setNewPageName('')
    setDirty(true)
  }

  function handleRenamePage() {
    if (!canEdit || !selectedPage) return
    setRenameInput(selectedPage.name)
    setRenameDialog({ pageId: selectedPage.id })
  }

  function handleRenameConfirm() {
    if (!renameDialog) return
    const name = renameInput.trim()
    if (name) {
      updatePage(renameDialog.pageId, (p) => ({ ...p, name }))
    }
    setRenameDialog(null)
  }

  function handleDeletePage() {
    if (!canEdit || !selectedPage) return
    const pageId = selectedPage.id
    setConfirmation({
      title: tPosLayout('confirm.deletePageTitle'),
      description: tPosLayout('confirm.deletePage', {
        name: selectedPage.name,
      }),
      confirmLabel: tPosLayout('actions.deletePage'),
      destructive: true,
      onConfirm: () => {
        setLayout((prev) => ({
          ...prev,
          pages: prev.pages.filter((p) => p.id !== pageId),
        }))
        setDirty(true)
      },
    })
  }

  function applyGridResize(nextCols: number, nextRows: number) {
    if (!selectedPage) return
    updatePage(selectedPage.id, (p) => ({
      ...p,
      grid: { columns: nextCols, rows: nextRows },
      tiles: p.tiles.filter((t) => t.col <= nextCols && t.row <= nextRows),
    }))
  }

  function handleGridResize(columns: number, rows: number) {
    if (!canEdit || !selectedPage) return
    const nextCols = clampGridValue(
      columns,
      GRID_LIMITS.columns.min,
      GRID_LIMITS.columns.max,
    )
    const nextRows = clampGridValue(
      rows,
      GRID_LIMITS.rows.min,
      GRID_LIMITS.rows.max,
    )
    const outOfBounds = selectedPage.tiles.filter(
      (t) => t.col > nextCols || t.row > nextRows,
    )
    if (outOfBounds.length > 0) {
      setConfirmation({
        title: tPosLayout('confirm.gridResizeTitle'),
        description: tPosLayout('confirm.gridResize', {
          count: outOfBounds.length,
        }),
        destructive: true,
        onConfirm: () => applyGridResize(nextCols, nextRows),
      })
      return
    }
    applyGridResize(nextCols, nextRows)
  }

  // --- Tile placement ---

  function placeTile(sku: string, row: number, col: number, name?: string) {
    if (!selectedPage) return
    const defaultColor = TILE_COLORS[0].key
    // The label is only a fallback for the POS when a product cannot be resolved there, so prefer
    // the name the caller acted on over anything we may or may not have resolved yet.
    const resolvedName = name || productMap.get(sku)?.name
    updatePage(selectedPage.id, (p) => ({
      ...p,
      tiles: [
        ...p.tiles,
        {
          id: newId(),
          sku,
          row,
          col,
          color: defaultColor,
          label: resolvedName || sku,
        },
      ],
    }))
  }

  function handleDrop(source: Record<string, unknown>, cell: GridCellData) {
    if (!canEdit || !selectedPage) return

    // Tile-to-tile swap
    if (source.type === 'tile') {
      const tileId = String(source.tileId)
      const tile = selectedPage.tiles.find((t) => t.id === tileId)
      if (!tile || (tile.row === cell.row && tile.col === cell.col)) return
      const targetTile = findTileByPosition(selectedPage, cell.row, cell.col)
      updatePage(selectedPage.id, (p) => ({
        ...p,
        tiles: p.tiles.map((t) => {
          if (t.id === tileId) return { ...t, row: cell.row, col: cell.col }
          if (targetTile && t.id === targetTile.id)
            return { ...t, row: tile.row, col: tile.col }
          return t
        }),
      }))
      return
    }

    // Product drop
    if (source.type === 'product') {
      const sku = String(source.sku)
      const droppedName =
        typeof source.name === 'string' ? source.name : undefined
      const existing = selectedPage.tiles.find((t) => t.sku === sku)
      const targetTile = findTileByPosition(selectedPage, cell.row, cell.col)

      if (targetTile) {
        toast.error(tPosLayout('errors.cellOccupied'))
        return
      }

      // Move existing tile
      if (existing) {
        updatePage(selectedPage.id, (p) => ({
          ...p,
          tiles: p.tiles.map((t) =>
            t.id === existing.id ? { ...t, row: cell.row, col: cell.col } : t,
          ),
        }))
        return
      }

      // New placement — offer to activate first. `active` comes from the drag source, which is the
      // list the user actually dragged from, so the prompt no longer depends on a lookup having
      // resolved this SKU.
      if (source.active === false) {
        setActivationPending({
          sku,
          productName: droppedName || sku,
          row: cell.row,
          col: cell.col,
        })
        return
      }

      placeTile(sku, cell.row, cell.col, droppedName)
    }
  }

  /**
   * Place a planned product the layout is missing onto the current page's next free cell.
   *
   * Goes through the drop path a drag from the library takes, so activation, occupied cells and
   * duplicate placements keep one set of rules.
   */
  function handlePlaceMissing(product: MissingMenuProduct) {
    if (!canEdit || !selectedPage) return
    const cell = findNextFreeCell(selectedPage)
    if (!cell) {
      toast.error(
        tPosLayout('menuDiff.noFreeCell', { page: selectedPage.name }),
      )
      return
    }
    handleDrop(
      {
        type: 'product',
        sku: product.placementSku,
        name: product.label,
        active: product.active,
      },
      cell,
    )
  }

  function handleRemoveTile(tileId: string) {
    if (!canEdit || !selectedPage) return
    updatePage(selectedPage.id, (p) => ({
      ...p,
      tiles: p.tiles.filter((t) => t.id !== tileId),
    }))
  }

  function handleColorChange(tileId: string, color: string) {
    if (!canEdit || !selectedPage) return
    updatePage(selectedPage.id, (p) => ({
      ...p,
      tiles: p.tiles.map((t) => (t.id === tileId ? { ...t, color } : t)),
    }))
  }

  function toggleOpenPriceButton(sku: string, enabled: boolean) {
    const current = layout.openPriceButtons ?? []
    const next = enabled ? [...current, sku] : current.filter((s) => s !== sku)
    setLayout({ ...layout, openPriceButtons: next })
    setDirty(true)
  }

  // --- Checkout add-ons ---

  const addonSkus = layout.checkoutAddonSkus ?? []
  // Judging eligibility while the lookup is still in flight would flash every entry as ineligible;
  // the save button is disabled during that window anyway (see `resolvePending` below).
  const addonIneligibleSkus = resolvePending
    ? []
    : findIneligibleAddonSkus(addonSkus, tileProductFor)

  function handleAddAddon(sku: string) {
    if (addonSkus.includes(sku)) return
    setLayout({ ...layout, checkoutAddonSkus: [...addonSkus, sku] })
    setDirty(true)
  }

  function handleRemoveAddon(sku: string) {
    setLayout({
      ...layout,
      checkoutAddonSkus: addonSkus.filter((s) => s !== sku),
    })
    setDirty(true)
  }

  function handleMoveAddon(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= addonSkus.length) return
    const next = [...addonSkus]
    ;[next[index], next[target]] = [next[target], next[index]]
    setLayout({ ...layout, checkoutAddonSkus: next })
    setDirty(true)
  }

  function doSave() {
    saveLayout.mutate(layout)
  }

  function handleSave() {
    if (!canEdit || !selectedRestaurantId) return

    // Add-on issues hard-block: unlike tiles, there is no "save anyway" path — an ineligible SKU
    // simply cannot be offered at checkout. The inline error on the card already explains why.
    if (addonIneligibleSkus.length > 0) return

    // Warn about tiles that won't sync to POS. Save is disabled while the lookup is in flight, so
    // this counts resolved facts only — it never guesses from missing data.
    let issueCount = 0
    for (const page of layout.pages) {
      for (const tile of page.tiles) {
        if (isBlockingTileState(getTileState(tileProductFor(tile.sku))))
          issueCount++
      }
    }
    if (issueCount > 0) {
      setConfirmation({
        title: tPosLayout('confirm.saveWithIssuesTitle'),
        description: tPosLayout('confirm.saveWithIssues', {
          count: issueCount,
        }),
        onConfirm: doSave,
      })
      return
    }

    doSave()
  }

  // --- Activation prompt ---

  async function handleActivateAndPlace() {
    if (!activationPending) return
    setActivating(true)
    try {
      await productsApi.update(activationPending.sku, { active: true })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      placeTile(
        activationPending.sku,
        activationPending.row,
        activationPending.col,
        activationPending.productName,
      )
      setActivationPending(null)
    } catch {
      toast.error(tPosLayout('errors.activationFailed'))
    } finally {
      setActivating(false)
    }
  }

  // --- Price dialog ---

  function handleOpenPriceDialog(sku: string) {
    const product = productMap.get(sku)
    // The tile menu hides this action for unresolved tiles, so an empty field here means the
    // product genuinely has no price — not that we failed to read one.
    if (!product) return
    const current = product.currentPrice
    setPriceDialog({
      sku,
      productName: product.name,
    })
    setPriceInput(current && current > 0 ? (current / 100).toFixed(2) : '')
  }

  async function handleSavePrice() {
    if (!priceDialog || !selectedRestaurantId) return
    const cents = Math.round(parseFloat(priceInput) * 100)
    if (!cents || cents <= 0) {
      toast.error(tPosLayout('errors.invalidPrice'))
      return
    }
    setSavingPrice(true)
    try {
      await pricesApi.set(priceDialog.sku, selectedRestaurantId, cents)
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success(tPosLayout('toast.priceSaved'))
      setPriceDialog(null)
    } catch {
      toast.error(tPosLayout('toast.priceFailed'))
    } finally {
      setSavingPrice(false)
    }
  }

  // --- Grid rendering ---

  const gridCells = []
  if (selectedPage) {
    for (let row = 1; row <= selectedPage.grid.rows; row += 1) {
      for (let col = 1; col <= selectedPage.grid.columns; col += 1) {
        const tile = findTileByPosition(selectedPage, row, col)
        gridCells.push(
          <GridCell
            key={`${row}-${col}`}
            row={row}
            col={col}
            tile={tile}
            tileProduct={tile ? tileProductFor(tile.sku) : undefined}
            onDrop={handleDrop}
            onRemove={handleRemoveTile}
            onColorChange={handleColorChange}
            onSetPrice={handleOpenPriceDialog}
            canEdit={canEdit}
          />,
        )
      }
    }
  }

  const panelWidth = 360

  return (
    <PermissionGate action={ACTIONS.POS_LAYOUT_VIEW}>
      <div className="flex flex-col h-[calc(100vh-4rem-3rem)]">
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 pb-6">
          <div>
            <h1 className="text-3xl font-bold">{tPosLayout('title')}</h1>
            <p className="text-muted-foreground mt-1">
              {tPosLayout('subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* The diff spans every page, and a restaurant with no page at all is precisely when
              everything is missing — so this belongs in the header, not in a page's toolbar. */}
            {selectedRestaurantId && canViewMenus && (
              <Button
                variant="outline"
                onClick={() => setDrawer('diff')}
                className={
                  missingFromLayout.length > 0
                    ? 'border-warning/50 bg-warning/10'
                    : undefined
                }
              >
                <AlertTriangle
                  className={`mr-2 h-4 w-4 ${
                    missingFromLayout.length > 0
                      ? 'text-warning'
                      : 'text-muted-foreground'
                  }`}
                />
                {tPosLayout('menuDiff.title')}
                {missingFromLayout.length > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-primary-foreground">
                    {missingFromLayout.length}
                  </span>
                )}
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={
                !dirty ||
                !selectedRestaurantId ||
                saveLayout.isPending ||
                resolvePending ||
                !canEdit ||
                addonIneligibleSkus.length > 0
              }
            >
              <Save className="mr-2 h-4 w-4" />
              {tPosLayout('actions.save')}
            </Button>
          </div>
        </div>

        {/* Main grid + library panel */}
        <div
          className="grid gap-6 flex-1 min-h-0 transition-[grid-template-columns] duration-300"
          style={{
            gridTemplateColumns: drawer
              ? `minmax(0, 1fr) ${panelWidth}px`
              : 'minmax(0, 1fr) 0px',
            gridTemplateRows: 'minmax(0, 1fr)',
          }}
        >
          <div className="space-y-6 min-w-0 overflow-y-auto">
            {/* Restaurant selector + add page */}
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-md">
                <Label htmlFor="restaurant-select">
                  {tPosLayout('labels.restaurant')}
                </Label>
                <Select
                  value={selectedRestaurantId?.toString() || 'none'}
                  onValueChange={(v) =>
                    setSelectedRestaurantId(v === 'none' ? null : Number(v))
                  }
                >
                  <SelectTrigger id="restaurant-select" className="mt-1.5">
                    <SelectValue
                      placeholder={tPosLayout('labels.selectRestaurant')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {tPosLayout('labels.selectRestaurant')}
                    </SelectItem>
                    {restaurants.map((r) => (
                      <SelectItem key={r.id} value={r.id.toString()}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  placeholder={tPosLayout('labels.newPagePlaceholder')}
                  disabled={!selectedRestaurantId || !canEdit}
                />
                <Button
                  onClick={handleAddPage}
                  disabled={
                    !selectedRestaurantId || !newPageName.trim() || !canEdit
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {tPosLayout('actions.addPage')}
                </Button>
              </div>
            </div>

            {/* Content area */}
            {isLoading && selectedRestaurantId ? (
              <div className="text-sm text-muted-foreground">
                {tPosLayout('labels.loadingLayout')}
              </div>
            ) : !selectedRestaurantId ? (
              <Card>
                <CardContent className="pt-6 text-sm text-muted-foreground">
                  {tPosLayout('labels.selectRestaurantHint')}
                </CardContent>
              </Card>
            ) : (
              <>
                {/* A failed lookup must say so. Rendering every tile as a problem instead would
                    repeat the bug this replaced: a data failure dressed up as product facts. */}
                {lookupFailed && (
                  <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {tPosLayout('errors.productLookupFailed')}
                  </div>
                )}
                {menuProductsFailed && (
                  <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {tPosLayout('errors.menuProductsFailed')}
                  </div>
                )}
                <Tabs
                  value={selectedPageId || ''}
                  onValueChange={setSelectedPageId}
                >
                  <TabsList className="w-full justify-start">
                    {layout.pages.map((p) => (
                      <TabsTrigger key={p.id} value={p.id}>
                        {p.name} ({p.tiles.length})
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {selectedPage ? (
                  <Card>
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <h2 className="text-lg font-semibold">
                            {tPosLayout('labels.pageProductsTitle', {
                              name: selectedPage.name,
                            })}
                          </h2>
                          <p className="text-sm text-muted-foreground">
                            {tPosLayout('labels.pageProductsSubtitle')}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2 text-sm">
                            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                            <span>{tPosLayout('labels.grid')}</span>
                            <Input
                              type="number"
                              min={GRID_LIMITS.columns.min}
                              max={GRID_LIMITS.columns.max}
                              value={selectedPage.grid.columns}
                              onChange={(e) =>
                                handleGridResize(
                                  Number(e.target.value),
                                  selectedPage.grid.rows,
                                )
                              }
                              className="w-20"
                              disabled={!canEdit}
                            />
                            <span>{tPosLayout('labels.gridSeparator')}</span>
                            <Input
                              type="number"
                              min={GRID_LIMITS.rows.min}
                              max={GRID_LIMITS.rows.max}
                              value={selectedPage.grid.rows}
                              onChange={(e) =>
                                handleGridResize(
                                  selectedPage.grid.columns,
                                  Number(e.target.value),
                                )
                              }
                              className="w-20"
                              disabled={!canEdit}
                            />
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => setDrawer('library')}
                            disabled={!canEdit}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {tPosLayout('actions.addProduct')}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={tPosLayout(
                                  'actions.pageActionsAria',
                                )}
                                disabled={!canEdit}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={handleRenamePage}
                                disabled={!canEdit}
                              >
                                {tPosLayout('actions.renamePage')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={handleDeletePage}
                                className="text-destructive"
                                disabled={!canEdit}
                              >
                                {tPosLayout('actions.deletePage')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <div
                        className="grid gap-3"
                        style={{
                          gridTemplateColumns: `repeat(${selectedPage.grid.columns}, minmax(0, 1fr))`,
                        }}
                      >
                        {gridCells}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="pt-6 text-sm text-muted-foreground">
                      {tPosLayout('labels.pageEmpty')}
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Open-price buttons config */}
            {selectedRestaurantId && openPriceProducts.length > 0 && (
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <PenLine className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-lg font-semibold">
                      {tPosLayout('openPriceButtons.title')}
                    </h2>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {tPosLayout('openPriceButtons.description')}
                  </p>
                  {openPriceTruncated && (
                    <p className="text-xs text-muted-foreground">
                      {tPosLayout('openPriceButtons.truncated', {
                        shown: openPriceProducts.length,
                        total: openPriceData?.total ?? 0,
                      })}
                    </p>
                  )}
                  <div className="space-y-2">
                    {openPriceProducts.map((product) => {
                      const enabled = (layout.openPriceButtons ?? []).includes(
                        product.sku,
                      )
                      return (
                        <div
                          key={product.sku}
                          className="flex items-center justify-between rounded-lg border px-4 py-3"
                        >
                          <div>
                            <div className="text-sm font-medium">
                              {product.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {product.sku} · {product.vatCode}
                            </div>
                          </div>
                          <Switch
                            checked={enabled}
                            onCheckedChange={(checked) =>
                              toggleOpenPriceButton(product.sku, checked)
                            }
                            disabled={!canEdit}
                          />
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Checkout add-on config */}
            {selectedRestaurantId && (
              <CheckoutAddonsCard
                restaurantId={selectedRestaurantId}
                addonSkus={addonSkus}
                tileProductFor={tileProductFor}
                ineligibleSkus={addonIneligibleSkus}
                onAdd={handleAddAddon}
                onRemove={handleRemoveAddon}
                onMove={handleMoveAddon}
                canEdit={canEdit}
              />
            )}
          </div>

          {/* Right-hand column: the product library and the menu diff, one at a time. Both stay
              mounted so the library keeps its search and filters while the diff is open — and the
              closed one must let clicks through, or it swallows every interaction of the open one. */}
          <div className="relative h-full min-h-0">
            <div
              className={`absolute inset-0 ${drawer === 'library' ? '' : 'pointer-events-none'}`}
            >
              <ProductLibrary
                open={drawer === 'library'}
                onClose={() => setDrawer(null)}
                products={products}
                total={productsData?.total ?? 0}
                categories={categories}
                loading={productsLoading}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                showInactive={showInactive}
                onShowInactiveChange={setShowInactive}
                onLoadMore={() => setLibraryPageSize((prev) => prev + 30)}
                truncated={libraryTruncated}
                canEdit={canEdit}
              />
            </div>
            <div
              className={`absolute inset-0 ${drawer === 'diff' ? '' : 'pointer-events-none'}`}
            >
              <MissingFromLayoutPanel
                open={drawer === 'diff'}
                onClose={() => setDrawer(null)}
                items={missingFromLayout}
                loading={menuProductsLoading}
                plannedCount={menuProductsData?.items.length ?? 0}
                failed={menuProductsFailed}
                windowTo={menuProductsData?.to}
                targetPageName={selectedPage?.name}
                onPlace={handlePlaceMissing}
                canEdit={canEdit}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Generic confirmation dialog */}
      <AlertDialog
        open={!!confirmation}
        onOpenChange={(open) => {
          if (!open) setConfirmation(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmation?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {tPosLayout('activate.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmation?.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
              onClick={() => {
                confirmation?.onConfirm()
                setConfirmation(null)
              }}
            >
              {confirmation?.confirmLabel || tPosLayout('activate.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename page dialog */}
      <Dialog
        open={!!renameDialog}
        onOpenChange={(open) => {
          if (!open) setRenameDialog(null)
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{tPosLayout('confirm.renamePage')}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={renameInput}
              onChange={(e) => setRenameInput(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameConfirm()
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(null)}>
              {tPosLayout('priceDialog.cancel')}
            </Button>
            <Button
              onClick={handleRenameConfirm}
              disabled={!renameInput.trim()}
            >
              {tPosLayout('actions.renamePage')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activation prompt */}
      <AlertDialog
        open={!!activationPending}
        onOpenChange={(open) => {
          if (!open) setActivationPending(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tPosLayout('activate.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {tPosLayout('activate.description', {
                name: activationPending?.productName ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={activating}>
              {tPosLayout('activate.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleActivateAndPlace}
              disabled={activating}
            >
              {tPosLayout('activate.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Price dialog */}
      <Dialog
        open={!!priceDialog}
        onOpenChange={(open) => {
          if (!open) setPriceDialog(null)
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{tPosLayout('priceDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">
              {priceDialog?.productName}
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-input">
                {tPosLayout('priceDialog.label')}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  €
                </span>
                <Input
                  id="price-input"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  className="pl-7"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSavePrice()
                  }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPriceDialog(null)}
              disabled={savingPrice}
            >
              {tPosLayout('priceDialog.cancel')}
            </Button>
            <Button
              onClick={handleSavePrice}
              disabled={savingPrice || !priceInput}
            >
              {tPosLayout('priceDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PermissionGate>
  )
}
